import React, { useRef, useState } from 'react';
import { Text, Box, Checkbox, Drawer, IconAdd, Inline, MenuItem, MenuRenderer, Stack, IconBookmark } from 'braid-design-system';
import { useUserPermissionAndMenu } from '../contexts/UserPermissionAndMenuProvider';
import { useNavigate } from 'react-router-dom';
import { updateUserFavouriteMenu } from '../services/menuService';
import { useToast } from '../hooks/useToast';

const FabButton: React.FC = () => {
    const [fabPos, setFabPos] = useState({ x: 32, y: window.innerHeight - 88 });
    const dragging = useRef(false);
    const offset = useRef({ x: 0, y: 0 });
    const { userFavoriteMenus, userMenus, setRefreshTrigger } = useUserPermissionAndMenu();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [favouriteIds, setFavouriteIds] = useState<number[]>([]);
    const [savingMenus, setSavingMenus] = useState<{
        [menuId: number]: boolean
    }>();
    const toast = useToast();

    React.useEffect(() => {
        setFavouriteIds(userFavoriteMenus?.map(menu => menu.id).filter((id): id is number => id !== null && id !== undefined) || []);
    }, [userFavoriteMenus]);

    const onMouseDown = (e: React.MouseEvent) => {
        dragging.current = true;
        offset.current = {
            x: e.clientX - fabPos.x,
            y: e.clientY - fabPos.y,
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };
    const onMouseMove = (e: MouseEvent) => {
        if (!dragging.current) return;
        setFabPos({
            x: Math.min(Math.max(e.clientX - offset.current.x, 16), window.innerWidth - 72),
            y: Math.min(Math.max(e.clientY - offset.current.y, 16), window.innerHeight - 72),
        });
    };
    const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
    };

    const handleMenuItemClick = (to: string) => {
        navigate(to);
    };

    const handleCheckboxChange = (menuId: number) => {
        setFavouriteIds(prev => {
            if (prev.includes(menuId)) {
                return prev.filter(id => id !== menuId);
            } else {
                return [...prev, menuId];
            }
        });
        setSavingMenus(prev => ({
            ...prev,
            [menuId]: true,
        }));
        updateUserFavouriteMenu(menuId, !favouriteIds.includes(menuId)).catch(
            error => {
                toast.showError(error instanceof Error ? error.message : 'An error occurred while updating favourite menu');
            }
        ).finally(() => {
            setSavingMenus(prev => ({
                ...prev,
                [menuId]: false,
            }));
        });
    }

    return (
        <>
            <Box style={{ position: 'fixed' }}>
                <Inline space="none">
                    <MenuRenderer
                        offsetSpace="small"
                        trigger={(triggerProps, { open }) => (
                            <Box {...triggerProps}
                                background='brand'
                                style={{
                                    position: 'fixed',
                                    left: fabPos.x,
                                    top: fabPos.y,
                                    zIndex: 1000,
                                    borderRadius: '50%',
                                    width: '56px',
                                    height: '56px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    fontSize: '2rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    userSelect: 'none',
                                }}
                                onMouseDown={onMouseDown}>
                                <IconBookmark active={open} />
                            </Box>
                        )}
                    >
                        {userFavoriteMenus?.map(menu => (
                            <MenuItem key={menu.id} onClick={() => handleMenuItemClick(menu.to_url!)}>
                                {menu.label}
                            </MenuItem>
                        ))}
                        <MenuItem key="all-menus" onClick={() => {
                            setDrawerOpen(true);
                        }}
                            icon={<IconAdd />}
                        >
                            Add Favourite Menus
                        </MenuItem>
                    </MenuRenderer>
                </Inline>
            </Box>
            {/* Drawer for Add Favourite Menus */}
            {/* State and logic for Drawer should be added at the top of the component */}
            {/* Example implementation below, you can adjust as needed */}

            <Drawer
                title="Add Favourite Menus"
                width="small"
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false)
                    setRefreshTrigger(true);
                }}
            >
                <Stack space="medium">
                    {
                        (userMenus || []).filter(menu => menu.has_menu)
                            .filter(menu => menu.parent_id === null)
                            .map(menu => {
                                const childMenus = (userMenus || []).filter(menu => menu.has_menu).filter(child => child.parent_id === menu.id);
                                if (childMenus.length > 0) {
                                    return (
                                        <Stack key={menu.id} space="small">
                                            <Text weight="strong">{menu.label}</Text>
                                            <Box paddingLeft="large">
                                                <Stack space="small">
                                                    {childMenus.map(childMenu => (
                                                        <Inline key={childMenu.id} space="small" alignY="center">
                                                            <Checkbox
                                                                key={childMenu.id}
                                                                label={String(childMenu.label)}
                                                                checked={favouriteIds.includes(childMenu.id!)}
                                                                onChange={() => handleCheckboxChange(childMenu.id!)}
                                                            />
                                                            {savingMenus?.[childMenu.id!] && <Text size="xsmall" tone="caution">Saving...</Text>}
                                                        </Inline>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    );
                                } else {
                                    return (
                                        <Inline key={menu.id} space="small" alignY="center">
                                            <Checkbox
                                                key={menu.id}
                                                label={String(menu.label)}
                                                checked={favouriteIds.includes(menu.id!)}
                                                onChange={() => handleCheckboxChange(menu.id!)}
                                            />
                                            {savingMenus?.[menu.id!] && <Text size="xsmall" tone="caution">Saving...</Text>}
                                        </Inline>
                                    );
                                }
                            })}
                </Stack>
            </Drawer>

        </>
    );
};

export default FabButton;
